import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Spinner, useToast, useDisclosure } from "@chakra-ui/react";
import { FiFolder, FiCheckSquare, FiAlertCircle, FiActivity } from "react-icons/fi";
import { api, logout } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import TaskDetailDrawer from "../Tasks/TaskDetailDrawer";
import { Card, CardHeader, Stat, StatusPill, PriorityPill, EmptyState, Skeleton } from "../../Components/ui";

function ActivityIcon({ action }) {
  const a = action || "";
  const map = {
    "task.create": "📝", "task.update": "✏️", "task.move": "🔄", "task.delete": "🗑️", "task.comment": "💬",
    "project.create": "📁", "project.update": "🔧", "project.delete": "🗑️",
    "user.signup": "👋", "user.login": "🔓", "user.login_mfa": "🔐",
    "user.create": "👤", "user.update": "✏️", "user.delete": "🗑️",
    "team.create": "👥", "team.update": "🔧",
    "department.create": "🏢", "department.update": "🔧",
    "invite.create": "✉️",
  };
  return <span>{map[a] || "•"}</span>;
}

function describeAction(log) {
  const map = {
    "task.create": "created a task", "task.update": "updated a task",
    "task.move": "moved a task", "task.delete": "deleted a task", "task.comment": "commented on a task",
    "project.create": "created a project", "project.update": "updated a project", "project.delete": "deleted a project",
    "user.signup": "signed up", "user.login": "signed in", "user.login_mfa": "signed in with MFA",
    "user.create": "added a user", "user.update": "updated a user", "user.delete": "removed a user",
    "user.force_logout": "forced a sign-out",
    "user.mfa_enabled": "enabled MFA", "user.mfa_disabled": "disabled MFA",
    "team.create": "created a team", "team.update": "updated a team", "team.delete": "deleted a team",
    "department.create": "created a department", "department.update": "updated a department", "department.delete": "deleted a department",
    "invite.create": "sent an invite", "invite.revoke": "revoked an invite",
  };
  return map[log.action] || log.action;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openTaskId, setOpenTaskId] = useState(null);
  const drawer = useDisclosure();
  const username = localStorage.getItem("username") || "there";

  useShellHeader({ title: `Welcome back, ${username.split(" ")[0]}` }, [username]);

  // Bitrix task-management dashboard. HR widgets live in HRDashboard.jsx, served
  // only by the standalone HR build (REACT_APP_MODE=hr).
  async function load() {
    setLoading(true);
    try {
      const d = await api("/dashboard");
      setData(d);
    } catch (e) {
      if (e.status === 401) { logout(); navigate("/login"); return; }
      toast({ title: e.message, status: "error" });
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading || !data) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
        <Skeleton.Card /><Skeleton.Card /><Skeleton.Card /><Skeleton.Card />
      </div>
    );
  }

  const { stats, myTasks, activeProjects, recentActivity, tasksByStatus } = data;
  const totalTasks = stats.tasks.total;
  const donePct = totalTasks ? Math.round((stats.tasks.done / totalTasks) * 100) : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Task/project KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
        <Stat
          label="Active Projects" value={stats.projects.active}
          hint={`${stats.projects.total} total`} icon={FiFolder} tone="success"
        />
        <Stat
          label="Open Tasks" value={stats.tasks.open}
          hint={`${stats.tasks.done} done`} icon={FiCheckSquare} tone="success"
        />
        <Stat
          label="Overdue" value={stats.tasks.overdue}
          hint={stats.tasks.overdue ? "needs attention" : "all clear"}
          icon={FiAlertCircle} tone={stats.tasks.overdue ? "danger" : "success"}
        />
        <Stat
          label="Completion" value={`${donePct}%`}
          hint={`${stats.tasks.done} / ${totalTasks} tasks`}
          icon={FiActivity} tone="success"
        />
      </div>

      {/* My tasks + Active projects */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <Card padding="lg">
          <CardHeader
            title="My open tasks"
            action={
              <RouterLink to="/tasks" style={{ fontSize: 12, color: "var(--accent-text)", fontWeight: 500 }}>
                View all →
              </RouterLink>
            }
          />
          {myTasks.length === 0 ? (
            <EmptyState icon="🎉" title="All clear" description="You have no open tasks." />
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {myTasks.map((t, i) => {
                const overdue = t.dueDate && new Date(t.dueDate) < new Date() && t.status !== "done";
                return (
                  <div
                    key={t._id}
                    onClick={() => { setOpenTaskId(t._id); drawer.onOpen(); }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-subtle)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    style={{
                      display: "flex", alignItems: "center", gap: 12, padding: "10px 8px",
                      borderTop: i === 0 ? "none" : "1px solid var(--border-subtle)",
                      borderRadius: 8, cursor: "pointer", transition: "background 120ms",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {t.title}
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                        {t.projectId && (
                          <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                            <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 9999, background: t.projectId.color || "#999", marginRight: 6, verticalAlign: "middle" }} />
                            {t.projectId.name}
                          </span>
                        )}
                        <StatusPill status={t.status} />
                        {t.priority !== "normal" && <PriorityPill priority={t.priority} />}
                      </div>
                    </div>
                    {t.dueDate && (
                      <div
                        title={new Date(t.dueDate).toLocaleString()}
                        className="tabular"
                        style={{
                          fontSize: 11,
                          color: overdue ? "#B91C1C" : "var(--text-tertiary)",
                          fontWeight: overdue ? 600 : 400,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {new Date(t.dueDate).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card padding="lg">
          <CardHeader
            title="Active projects"
            action={
              <RouterLink to="/projects" style={{ fontSize: 12, color: "var(--accent-text)", fontWeight: 500 }}>
                View all →
              </RouterLink>
            }
          />
          {activeProjects.length === 0 ? (
            <EmptyState icon="📁" title="No active projects" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {activeProjects.map((p, i) => (
                <RouterLink key={p._id} to={`/projects/${p._id}`} style={{ textDecoration: "none" }}>
                  <div
                    onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-subtle)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "10px 8px",
                      borderTop: i === 0 ? "none" : "1px solid var(--border-subtle)",
                      borderRadius: 8, transition: "background 120ms",
                    }}
                  >
                    <div style={{ width: 3, height: 28, background: p.color, borderRadius: 9999 }} />
                    <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {p.name}
                    </div>
                    <StatusPill status={p.status} />
                  </div>
                </RouterLink>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Activity + Tasks by status */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <Card padding="lg">
          <CardHeader title="Recent activity" />
          {recentActivity.length === 0 ? (
            <EmptyState icon="⌛" title="No activity yet" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {recentActivity.map((l) => (
                <div key={l._id} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ paddingTop: 2 }}><ActivityIcon action={l.action} /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                      <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{l.actorEmail || "system"}</span>
                      <span> {describeAction(l)}</span>
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{new Date(l.createdAt).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card padding="lg">
          <CardHeader title="Tasks by status" />
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {[
              { key: "todo", label: "To do", color: "#94A3B8" },
              { key: "in_progress", label: "In progress", color: "var(--accent-text)" },
              { key: "review", label: "In review", color: "#8B5CF6" },
              { key: "done", label: "Done", color: "#10B981" },
            ].map(({ key, label, color }) => {
              const n = tasksByStatus[key] || 0;
              const pct = totalTasks ? (n / totalTasks) * 100 : 0;
              return (
                <div key={key}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text-secondary)" }}>{label}</span>
                    <span className="tabular" style={{ fontSize: 12, color: "var(--text-tertiary)" }}>{n}</span>
                  </div>
                  <div style={{ height: 4, background: "var(--bg-subtle)", borderRadius: 9999, overflow: "hidden" }}>
                    <div style={{
                      width: `${pct}%`, height: "100%", background: color,
                      borderRadius: 9999, transition: "width 600ms cubic-bezier(0.4, 0, 0.2, 1)",
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <TaskDetailDrawer
        isOpen={drawer.isOpen}
        onClose={() => { drawer.onClose(); setOpenTaskId(null); load(); }}
        taskId={openTaskId}
        allUsers={[]}
        onChanged={() => load()}
      />
    </div>
  );
}

// HR widgets live in Pages/Dashboard/HRDashboard.jsx — served only by the
// standalone HR build. Kept here as legacy dead code only for reference
// during the split phase; safe to delete when the split is permanent.
// eslint-disable-next-line no-unused-vars
function HRDashboardSection({ hr }) {
  const { leaves, attendanceThisMonth, upcomingHolidays, recentNotices,
          absentToday, wfhToday, dailyQuote } = hr;

  const monthLabel = new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Daily quote */}
      {dailyQuote && (
        <Card padding="lg">
          <div style={{ fontStyle: "italic", fontSize: 15, color: "var(--text-primary)", lineHeight: 1.55 }}>
            "{dailyQuote.q}"
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: "var(--text-tertiary)" }}>— {dailyQuote.a}</div>
        </Card>
      )}

      {/* KPI strip — attendance this month */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        <MiniStat label="Present" value={attendanceThisMonth.present} color="#059669" sub={monthLabel} />
        <MiniStat label="WFH"     value={attendanceThisMonth.wfh}     color="#2563EB" sub={monthLabel} />
        <MiniStat label="Late"    value={attendanceThisMonth.late}    color="#D97706" sub={monthLabel} />
        <MiniStat label="Absent"  value={attendanceThisMonth.absent}  color="#DC2626" sub={monthLabel} />
        <MiniStat label="Leaves pending"  value={leaves.pending}  color="#7C3AED" />
        <MiniStat label="Leaves approved" value={leaves.approved} color="#059669" />
      </div>

      {/* 3-column row: holidays | notices | who's out */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        <Card padding="none">
          <CardHeader title="🎉 Upcoming holidays" />
          {upcomingHolidays.length === 0 ? (
            <div style={{ padding: 14, fontSize: 13, color: "var(--text-tertiary)" }}>None in the next few weeks.</div>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {upcomingHolidays.map((h) => (
                <li key={h._id} style={{ padding: "10px 14px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ fontSize: 13, color: "var(--text-primary)", fontWeight: 500 }}>{h.title}</span>
                  <span style={{ fontSize: 12, color: "var(--text-tertiary)", whiteSpace: "nowrap" }}>
                    {new Date(h.holidayDate).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card padding="none">
          <CardHeader title="📢 Recent notices" />
          {recentNotices.length === 0 ? (
            <div style={{ padding: 14, fontSize: 13, color: "var(--text-tertiary)" }}>Nothing new.</div>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {recentNotices.map((n) => (
                <li key={n._id} style={{ padding: "10px 14px", borderTop: "1px solid var(--border-subtle)" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", marginBottom: 2 }}>
                    {n.pinned ? "📌 " : ""}{n.title}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                    {new Date(n.createdAt).toLocaleDateString()} · {n.createdBy?.username || "—"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card padding="none">
          <CardHeader title="👥 Out today" />
          {absentToday.length === 0 && wfhToday.length === 0 ? (
            <div style={{ padding: 14, fontSize: 13, color: "var(--text-tertiary)" }}>Everyone's in.</div>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {absentToday.map((s, i) => (
                <li key={`a-${i}`} style={{ padding: "8px 14px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ fontSize: 13, color: "var(--text-primary)" }}>
                    {`${s.firstName || ""} ${s.lastName || ""}`.trim() || s.username}
                  </span>
                  <span style={{ fontSize: 11, color: s.reason === "leave" ? "#0369A1" : "#991B1B", fontWeight: 600, textTransform: "uppercase" }}>
                    {s.reason === "leave" ? "On Leave" : "Absent"}
                  </span>
                </li>
              ))}
              {wfhToday.map((s, i) => (
                <li key={`w-${i}`} style={{ padding: "8px 14px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ fontSize: 13, color: "var(--text-primary)" }}>
                    {`${s.firstName || ""} ${s.lastName || ""}`.trim() || s.username}
                  </span>
                  <span style={{ fontSize: 11, color: "#3730A3", fontWeight: 600, textTransform: "uppercase" }}>WFH</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function MiniStat({ label, value, color, sub }) {
  return (
    <Card padding="md">
      <div style={{
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em",
        color: "var(--text-tertiary)", fontWeight: 600, marginBottom: 4,
      }}>{label}</div>
      <div className="tabular" style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 4 }}>{sub}</div>}
    </Card>
  );
}
